/**
 * The vault key input.
 *
 * Every box in this app that asks for words now runs the seed-phrase guard —
 * and this one needs it most.
 */

import { useCallback, useId, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { inspect, recordVerdict, type GuardVerdict } from '../guards/bip39';
import { springSnappy, tapFeedback } from '../design/motion';
import { checkPassphraseShape, suggestionsFor } from '../vault/passphrase';
import { Button } from './Button';
import { Sheet } from './Sheet';
import { useLanguage } from '../i18n/context';

export interface VaultKeyFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  /**
   * `masked` for unlocking, where someone may be watching. `visible` when the
   * user is copying from their own paper and a typo costs them a slow KDF run
   * to find out about.
   */
  reveal?: 'masked' | 'visible';
  autoFocus?: boolean;
  /** Set while the KDF is running so the field cannot be edited mid-derive. */
  disabled?: boolean;
}

export function VaultKeyField({
  value,
  onChange,
  label,
  placeholder,
  reveal = 'masked',
  autoFocus,
  disabled,
}: VaultKeyFieldProps) {
  const id = useId();
  const { t } = useLanguage();
  const [blocked, setBlocked] = useState<GuardVerdict | null>(null);
  const pasted = useRef(false);

  const handle = useCallback(
    (next: string) => {
      const wasPaste = pasted.current;
      pasted.current = false;

      const verdict = inspect(next, { pasted: wasPaste });
      recordVerdict(verdict);

      if (verdict.level === 'block') {
        // Destroy first, explain second — the same rule as the hint fields.
        tapFeedback('error');
        onChange('');
        setBlocked(verdict);
        return;
      }
      onChange(next);
    },
    [onChange],
  );

  const shape = checkPassphraseShape(value);
  const firstUnknown = shape.unknownWords[0];
  const suggestions = firstUnknown ? suggestionsFor(firstUnknown) : [];

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-label text-ink-5">
        {label}
      </label>

      <input
        id={id}
        type={reveal === 'masked' ? 'password' : 'text'}
        value={value}
        onChange={(event) => handle(event.target.value)}
        onPaste={() => {
          pasted.current = true;
        }}
        placeholder={placeholder ?? t.unlock.keyPlaceholder}
        disabled={disabled}
        autoFocus={autoFocus}
        // Never autocomplete a vault key: the suggestion list is readable by
        // anything on the page and by anyone looking over a shoulder.
        name="vault-key"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-describedby={firstUnknown ? `${id}-problem` : undefined}
        aria-invalid={firstUnknown ? true : undefined}
        className="text-numeric w-full rounded-xl border border-ink-2 bg-ink-1 px-3.5 py-3 text-ink-7 placeholder:text-ink-4 focus:border-accent focus:outline-none disabled:opacity-50"
      />

      {/* Inline and continuous: a typo becomes "word 3 is not on the list"
          rather than "wrong key" after a second and a half of Argon2id. */}
      <AnimatePresence>
        {value.length > 0 && firstUnknown && (
          <motion.p
            id={`${id}-problem`}
            role="status"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={springSnappy}
            className="text-label text-due-soon"
          >
            {t.guards.vaultKeyUnknownWord(firstUnknown)}
            {suggestions.length > 0 && t.guards.vaultKeyDidYouMean(suggestions.join(', '))}
          </motion.p>
        )}
      </AnimatePresence>

      {value.length > 0 && !firstUnknown && shape.wordCount < 6 && (
        <p className="text-label text-ink-4">{t.guards.vaultKeyRemainingWords(6 - shape.wordCount)}</p>
      )}

      <Sheet
        open={blocked !== null}
        onClose={() => setBlocked(null)}
        title={t.guards.vaultKeyBlockedTitle}
      >
        <p className="text-body mb-4 text-ink-5">
          {t.guards.vaultKeyBlockedP1}
        </p>
        <p className="text-body mb-4 text-ink-5">
          {t.guards.vaultKeyBlockedP2}
        </p>
        <p className="text-body mb-5 text-ink-4">
          {t.guards.vaultKeyBlockedP3}
        </p>
        <Button size="lg" className="w-full" onClick={() => setBlocked(null)}>
          {t.common.gotIt}
        </Button>
      </Sheet>
    </div>
  );
}
